import AuthService from "vamos/cockpit/base/services/type/AuthService";
import Config from "vamos/cockpit/config/Config";
import Component from "vamos/cockpit/Component";
import ServerUnreachableScreen from "vamos/cockpit/login/ServerUnreachableScreen";

/**
 * Attaches the stateless authentication headers ("Authorization: Bearer <jwt>" and "X-Client-Guid: <guid>") to every request that targets the VAMOS
 * backend, by wrapping the browser's own fetch and XMLHttpRequest once at app startup. It also watches every such request for a status code above
 * 500, or for an outright network-level failure (the backend is not reachable at all, e.g. connection refused), and if the backend turns out to be
 * genuinely unreachable, switches the app into the "server unreachable" maintenance page (see `checkServerAvailability`).
 *
 * This is deliberately done at the transport level instead of in each API wrapper: the app talks to the backend through ApiService (fetch), the
 * deprecated DataExchange (jQuery.ajax), FileService (fetch) and the OData v4 model (XMLHttpRequest, fired before any controller code runs because
 * of "earlyRequests"). A single interception point is the only way to cover all of them without leaving a path that silently loses its token or an
 * outage undetected.
 *
 * Requests to anything other than the backend (UI5 resources, i18n bundles, env-config.json, ...) are passed through untouched, and an already
 * present header is never overwritten.
 *
 * @namespace vamos.cockpit.base.services.type
 */
export default class AuthInterceptor {

    private static installed = false;

    public static install(): void {
        if (AuthInterceptor.installed) {
            return;
        }
        AuthInterceptor.installed = true;
        AuthInterceptor.interceptFetch();
        AuthInterceptor.interceptXmlHttpRequest();
    }

    /**
     * The backend base url is read per request, not cached: env-config.json overrides are merged into Config asynchronously at startup and may
     * change the backend location after this interceptor is installed.
     */
    private static isBackendUrl(url: string): boolean {
        if (!url) {
            return false;
        }
        try {
            const target = new URL(url, window.location.href);
            const backend = new URL(Config.getConfigValue("BackendUrl") as string, window.location.href);
            const backendPath = backend.pathname.endsWith("/") ? backend.pathname : backend.pathname + "/";
            return target.origin === backend.origin && (target.pathname + "/").startsWith(backendPath);
        } catch {
            return false;
        }
    }

    private static interceptFetch(): void {
        const originalFetch = window.fetch.bind(window);
        window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
            const url = AuthInterceptor.getFetchUrl(input);
            if (!AuthInterceptor.isBackendUrl(url)) {
                return originalFetch(input, init);
            }
            const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
            const token = AuthService.getToken();
            if (token && !headers.has("Authorization")) {
                headers.set("Authorization", "Bearer " + token);
            }
            const clientGuid = AuthService.getClientGuid();
            if (clientGuid && !headers.has("X-Client-Guid")) {
                headers.set("X-Client-Guid", clientGuid);
            }
            const responsePromise = originalFetch(input, { ...init, headers });
            responsePromise.then(
                (response) => {
                    if (response.status > 500) {
                        void AuthInterceptor.checkServerAvailability();
                    }
                },
                () => { void AuthInterceptor.checkServerAvailability(); },
            );
            return responsePromise;
        };
    }

    private static getFetchUrl(input: RequestInfo | URL): string {
        if (typeof input === "string") {
            return input;
        }
        if (input instanceof URL) {
            return input.href;
        }
        return input.url;
    }

    private static interceptXmlHttpRequest(): void {
        const originalOpen = XMLHttpRequest.prototype.open;
        const originalSend = XMLHttpRequest.prototype.send;

        XMLHttpRequest.prototype.open = function(this: XMLHttpRequest, method: string, url: string | URL, ...rest: any[]) {
            (this as any).vamosRequestUrl = typeof url === "string" ? url : url.href;
            return originalOpen.apply(this, [method, url, ...rest] as any);
        } as typeof XMLHttpRequest.prototype.open;

        XMLHttpRequest.prototype.send = function(this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null) {
            if (AuthInterceptor.isBackendUrl((this as any).vamosRequestUrl as string)) {
                const token = AuthService.getToken();
                if (token) {
                    this.setRequestHeader("Authorization", "Bearer " + token);
                }
                const clientGuid = AuthService.getClientGuid();
                if (clientGuid) {
                    this.setRequestHeader("X-Client-Guid", clientGuid);
                }
                this.addEventListener("load", () => {
                    if (this.status > 500) {
                        void AuthInterceptor.checkServerAvailability();
                    }
                });
                this.addEventListener("error", () => {
                    void AuthInterceptor.checkServerAvailability();
                });
            }
            return originalSend.call(this, body);
        };
    }

    private static isCheckingAvailability = false;

    /**
     * Beyond the one-time reachability check Component runs at app start, this re-checks reachability
     * whenever any backend call (REST via ApiService/FileService, the OData v4 model, or the deprecated
     * jQuery-based DataExchange - all of which pass through this interceptor) comes back with a status
     * code above 500, or fails outright at the network level (e.g. the server is not reachable at all).
     * Only if the backend is genuinely unreachable does it switch the app into the "server unreachable"
     * maintenance page, instead of treating it as a one-off application error.
     *
     * The very first backend call of the app (LoginService#checkLogin, via "/user/login") happens
     * before the vamos.cockpit Component - and with it Component#enterServerUnreachableMode - even
     * exists, so Component.getComponent() returns undefined for it. ServerUnreachableScreen covers
     * exactly that window, rendering directly into #root with no dependency on a Component.
     *
     * Guarded by isCheckingAvailability: the reachability check itself is a fetch to "system/info",
     * which passes back through this same interceptor. While a server is fully down that fetch also
     * fails/comes back above 500, which would otherwise re-trigger this method on itself and never stop.
     */
    private static async checkServerAvailability(): Promise<void> {
        if (AuthInterceptor.isCheckingAvailability) {
            return;
        }
        AuthInterceptor.isCheckingAvailability = true;
        try {
            const component = Component.getComponent();
            if (component) {
                if (!(await component.isBackendAvailable())) {
                    component.enterServerUnreachableMode();
                }
                return;
            }
            if (!(await AuthInterceptor.isBackendReachable())) {
                ServerUnreachableScreen.getInstance().show();
            }
        } catch {
            // best-effort reachability check; must never affect the original request/response flow
        } finally {
            AuthInterceptor.isCheckingAvailability = false;
        }
    }

    private static async isBackendReachable(): Promise<boolean> {
        try {
            const response = await fetch(new Config().getRestUrl("system/info", true));
            return response.ok;
        } catch {
            return false;
        }
    }
}
